import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHudLines, useFeeVersions } from '../../hooks/useFees';
import { ConfirmPopup } from 'primereact/confirmpopup';
import { Toast } from 'primereact/toast';
import HudLines from './HudLines';
import { buildHudLineCopyDraft } from '../../utils/fee-helper';
import { FeeHudLineViewModel, CreateOrUpdateFeeHudLineViewModel } from '../../models/fees';

type HudLineEditorState =
  | { mode: 'create' }
  | { mode: 'edit'; hudLine: FeeHudLineViewModel }
  | { mode: 'copy'; hudLine: FeeHudLineViewModel };

const HudLinesContainer: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [selectedVersionId, setSelectedVersionId] = useState<number | null>(null);

  useEffect(() => {
    const versionIdParam = searchParams.get('versionId');
    if (versionIdParam) {
      const versionId = parseInt(versionIdParam, 10);
      if (!isNaN(versionId)) {
        setSelectedVersionId(versionId);
      }
    }
  }, [searchParams]);
  const [editor, setEditor] = useState<HudLineEditorState | null>(null);
  const toastRef = React.useRef<Toast>(null);

  const { versionsQuery } = useFeeVersions();
  const { hudLinesQuery, createHudLine, updateHudLine, deleteHudLine, importHudLines } = useHudLines(selectedVersionId);
  // Copies and new lines are always saved as drafts, so uniqueness is checked against the draft
  // lines even while a published version is being viewed. Published rows keep the numbers of the
  // draft rows they came from, so they are excluded, as is the number the edited line already has.
  const { hudLinesQuery: draftHudLinesQuery } = useHudLines(null);

  const editedHudLineNumber = editor?.mode === 'edit' ? editor.hudLine.hudLine : null;
  const existingHudLineNumbers = (draftHudLinesQuery.data ?? [])
    .filter((draftHudLine) => !draftHudLine.feeRulesVersionId && draftHudLine.hudLine !== editedHudLineNumber)
    .map((draftHudLine) => draftHudLine.hudLine)
    .filter((hudLineNumber): hudLineNumber is string => !!hudLineNumber);

  const handleCreate = () => {
    setEditor({ mode: 'create' });
  };

  const handleEdit = (hudLine: FeeHudLineViewModel) => {
    setEditor({ mode: 'edit', hudLine });
  };

  const handleCopy = (hudLine: FeeHudLineViewModel) => {
    setEditor({ mode: 'copy', hudLine: buildHudLineCopyDraft(hudLine) });
  };

  const handleSave = async (payload: CreateOrUpdateFeeHudLineViewModel) => {
    const isEdit = editor?.mode === 'edit';
    try {
      if (isEdit) {
        await updateHudLine({ id: editor.hudLine.feeHudLineId, payload });
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'HUD Line updated successfully',
          life: 3000,
        });
      } else {
        await createHudLine(payload);
        toastRef.current?.show({
          severity: 'success',
          summary: 'Success',
          detail: 'HUD Line created successfully',
          life: 3000,
        });
      }
      setEditor(null);
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        // apiClient's response interceptor already surfaces the API message on error.message.
        detail: error instanceof Error ? error.message : `Failed to ${isEdit ? 'update' : 'create'} HUD Line`,
        life: 5000,
      });
      console.error('Failed to save HUD Line:', error);
    }
  };

  const handleDelete = async (hudLineId: number) => {
    try {
      await deleteHudLine(hudLineId);
      toastRef.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'HUD Line deleted successfully',
        life: 3000,
      });
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Failed to delete HUD Line',
        life: 3000,
      });
      console.error('Failed to delete HUD Line:', error);
    }
  };

  const handleCancelEdit = () => {
    setEditor(null);
  };

  const handleImport = async (file: File) => {
    try {
      const result = await importHudLines(file);
      
      if (result.successfulRows > 0) {
        toastRef.current?.show({
          severity: 'success',
          summary: 'Import Completed',
          detail: `Successfully imported ${result.successfulRows} of ${result.totalRows} HUD lines`,
          life: 3000,
        });
      }
      
      return result;
    } catch (error) {
      toastRef.current?.show({
        severity: 'error',
        summary: 'Import Failed',
        detail: error instanceof Error ? error.message : 'Failed to import HUD lines',
        life: 5000,
      });
      throw error;
    }
  };

  return (
    <>
      <Toast ref={toastRef} />
      <ConfirmPopup />
      <HudLines
        hudLines={hudLinesQuery.data || []}
        versions={versionsQuery.data || []}
        loading={hudLinesQuery.isLoading || versionsQuery.isLoading}
        selectedVersionId={selectedVersionId}
        onVersionChange={setSelectedVersionId}
        onCreateClick={handleCreate}
        onEditClick={handleEdit}
        onCopyClick={handleCopy}
        onDeleteClick={handleDelete}
        onImport={handleImport}
        onBackClick={() => navigate(selectedVersionId ? `/fees?versionId=${selectedVersionId}` : '/fees')}
        editorMode={editor?.mode ?? null}
        selectedHudLine={editor && editor.mode !== 'create' ? editor.hudLine : null}
        existingHudLineNumbers={existingHudLineNumbers}
        onSave={handleSave}
        onCancelEdit={handleCancelEdit}
      />
    </>
  );
};

export default HudLinesContainer;

